import type {
  FieldPath,
  FieldPathValue,
  FieldValues,
  Validate,
  ValidateResult,
} from "react-hook-form";

type Validations = "number" | "required" | "date" | "time";
export type ValidationType = Validations | Validate<any, any>;
type Prop<
  FormType extends FieldValues,
  TFieldName extends FieldPath<FormType>,
> = {
  label?: string;
  value?: FieldPathValue<FormType, TFieldName>;
};

const dateRegEx = new RegExp(/^[\d]{4}-[\d]{2}-[\d]{2}$/);
const timeRegEx = new RegExp(/^(0\d|1\d|2[0-3]):[0-5]\d$/);
const numberRegEx = new RegExp(/^[+-]?\d*\.\d+$|^[+-]?\d+(\.\d*)?$/);
const valueIsString = (value: any): value is string =>
  typeof value === "string";

const date = <
  FormType extends FieldValues,
  TFieldName extends FieldPath<FormType>,
>(
  _props: Prop<FormType, TFieldName>
) => {
  const message = `Invalid date format. Please use format YYYY-MM-DD`;
  return (value: FieldPathValue<FormType, TFieldName>) =>
    valueIsString(value) && dateRegEx.test(value) ? true : message;
};
const time = <
  FormType extends FieldValues,
  TFieldName extends FieldPath<FormType>,
>(
  _props: Prop<FormType, TFieldName>
) => {
  const message = `Invalid time format. Please use format HH:MM`;
  return (value: FieldPathValue<FormType, TFieldName>) =>
    valueIsString(value) && timeRegEx.test(value) ? true : message;
};
const number = <
  FormType extends FieldValues,
  TFieldName extends FieldPath<FormType>,
>(
  props: Prop<FormType, TFieldName>
) => {
  const message = `Invalid ${props.label}! Not a valid number!`;
  return (value: FieldPathValue<FormType, TFieldName>) =>
    valueIsString(value) && numberRegEx.test(value) ? true : message;
};
const required = <
  FormType extends FieldValues,
  TFieldName extends FieldPath<FormType>,
>(
  props: Prop<FormType, TFieldName>
) => {
  const message = `${props.label} is required!`;
  return (value: FieldPathValue<FormType, TFieldName>): ValidateResult => {
    if (valueIsString(value) && !value.toString().trim()) return message;

    return true;
  };
};

export default function validator<
  FormType extends FieldValues,
  TFieldName extends FieldPath<FormType>,
>(props: Prop<FormType, TFieldName>, validations?: ValidationType[]) {
  const validate = validations?.reduce(
    (
      all: Record<
        string,
        | Validate<FieldPathValue<FieldValues, TFieldName>, FieldValues>
        | Record<
            string,
            Validate<FieldPathValue<FieldValues, TFieldName>, FieldValues>
          >
      >,
      validation: ValidationType,
      index
    ) => {
      if (typeof validation === "function")
        return { ...all, [`custom${index}`]: validation };
      switch (validation) {
        case "date":
          return { ...all, [validation]: date<FormType, TFieldName>(props) };
        case "time":
          return { ...all, [validation]: time<FormType, TFieldName>(props) };
        case "number":
          return { ...all, [validation]: number<FormType, TFieldName>(props) };
        case "required":
          return {
            ...all,
            [validation]: required<FormType, TFieldName>(props),
          };
      }
    },
    {} as Record<
      string,
      | Validate<FieldPathValue<FieldValues, TFieldName>, FieldValues>
      | Record<
          string,
          Validate<FieldPathValue<FieldValues, TFieldName>, FieldValues>
        >
    >
  );
  return { validate };
}
